import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateUserData } from "../../app/auth/authSlice";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import userDataService from "../../services/userDataService";

/**
 * Stripe Checkout return handler (success/cancel).
 * Dedicated route so redirects are not blocked by billing route or permission checks.
 */
function StripePaymentReturn() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isChildBranch = useIsChildBranch();
  const handledRef = useRef(false);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const run = async () => {
      const paymentResult = searchParams.get("payment");
      const sessionId = searchParams.get("session_id");

      if (paymentResult === "cancelled") {
        showErrorToast("Payment was cancelled.");
        setSearchParams({}, { replace: true });
        navigate(isChildBranch ? "/dashboard" : "/administration/Billing", { replace: true });
        return;
      }

      if (paymentResult !== "success") {
        navigate(isChildBranch ? "/dashboard" : "/administration/Billing", { replace: true });
        return;
      }

      if (!sessionId) {
        showErrorToast("Payment received but session details are missing. Check Billing for invoice status.");
        setSearchParams({}, { replace: true });
        navigate(isChildBranch ? "/dashboard" : "/administration/Billing", { replace: true });
        return;
      }

      try {
        const confirmRes = await axiosInstance.post(APIs.POST_CONFIRM_STRIPE_CHECKOUT, {
          sessionId,
        });
        if (confirmRes.data.status) {
          showSuccessToast(
            confirmRes.data.message || "Payment confirmed. Your subscription access has been updated."
          );
          try {
            const userData = await userDataService.fetchUserData();
            dispatch(updateUserData(userData));
          } catch (refreshErr) {
            console.error("Failed to refresh profile after payment:", refreshErr);
          }
        } else {
          showErrorToast(confirmRes.data.message || "Payment received but confirmation failed");
        }
      } catch (error) {
        showErrorToast(
          error.response?.data?.message ||
            "Payment received. Confirmation is pending — open Billing to refresh."
        );
      } finally {
        setSearchParams({}, { replace: true });
        navigate(isChildBranch ? "/dashboard" : "/administration/Billing", { replace: true });
        setProcessing(false);
      }
    };

    run();
  }, [searchParams, setSearchParams, navigate, dispatch, isChildBranch]);

  if (processing) {
    return <Preloader />;
  }

  return null;
}

export default StripePaymentReturn;
